// Story 6.26 (ADO-1205) — shared behaviour for the two account-recovery
// surfaces `ActivationLinkExpired` and `ForgotPassword` (they are clones, per
// their file headers + deferred-work.md). Resolving the two deferred a11y/UX
// items ONCE, here, rather than duplicating the fix across both:
//
//   AC1 — a persistently-mounted live region announcement. `announcement` is
//   '' until the `submitted` transition, then the surface's enumeration-safe
//   confirmation message. The component renders this into an always-mounted
//   `role="status"` node, so a screen reader observes a TEXT CHANGE on an
//   existing region (announced) rather than a freshly-inserted, already-
//   populated node (frequently skipped). Focus-to-heading is KEPT (see below).
//
//   AC2 — `onUseDifferentEmail` resets the surface in place: it releases the
//   permanently-latched `sentRef`, clears the announcement (so a repeat submit
//   re-announces), and flips back to the form — no full page reload. The email
//   value is preserved so a mistyped address can be edited (D-4).
//
// ENUMERATION-SAFE (NFR15): `onSubmit` flips to the confirmation FIRST, then
// fires the send without awaiting or branching on its result, so neither the
// message nor its timing can disclose account existence. Unchanged from the
// original components.

import { useEffect, useRef, useState, type FormEvent } from 'react';

export interface EmailRecoverySurface {
  /** Current email field value (controlled input). */
  email: string;
  /** Update the email field value. */
  onEmailChange: (value: string) => void;
  /** True once a (non-empty) email has been submitted — drives the view swap. */
  submitted: boolean;
  /**
   * Live-region text. '' until the submitted transition, then the confirmation
   * message. Render into a persistently-mounted `role="status"` node (AC1).
   */
  announcement: string;
  /** Focus target: the current view's heading (focused on mount + on transition). */
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  /** Form submit handler: trim → double-submit guard → enumeration-safe fire-and-forget. */
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  /** Reset the surface in place so a mistyped email can be re-entered (AC2). */
  onUseDifferentEmail: () => void;
}

/**
 * @param onRequest Enumeration-safe re-request/reset action for the entered
 *   email; resolves identically for any input. Null when no client/config
 *   exists yet — the surface still shows the generic confirmation.
 * @param confirmationMessage The account-existence-neutral message announced on
 *   the submitted transition and shown on the confirmation view.
 */
export function useEmailRecoverySurface(
  onRequest: ((email: string) => Promise<void>) | null,
  confirmationMessage: string,
): EmailRecoverySurface {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // '' until the submitted transition; the persistently-mounted live region
  // node then observes a text CHANGE and announces it (AC1).
  const [announcement, setAnnouncement] = useState('');
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Synchronous guard against a same-tick double submit: `submitted` is a state
  // schedule (the form only unmounts on the next render), so a second submit
  // dispatched before that render would otherwise fire a duplicate send.
  const sentRef = useRef(false);
  // Handle of the pending "announce on the next frame" callback (see onSubmit),
  // so it can be cancelled on reset or unmount and never lands a stale setState.
  const announceFrameRef = useRef<number | null>(null);

  // Move focus to the heading on mount and when the view switches (to the
  // confirmation on submit, and back to the form on "use a different email"),
  // so screen-reader / keyboard users land on the new content. Kept as the
  // ticket's stated mitigation — the live region is added alongside it (AC1).
  useEffect(() => {
    headingRef.current?.focus();
  }, [submitted]);

  // Cancel any pending announcement frame when the surface unmounts, so the
  // deferred setState below can never fire after teardown.
  useEffect(
    () => () => {
      if (announceFrameRef.current !== null) cancelAnimationFrame(announceFrameRef.current);
    },
    [],
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sentRef.current) return;
    // Trim before sending: autofill / paste can introduce surrounding
    // whitespace that the browser's `required`/`type="email"` validation does
    // not strip, and Auth0 would then silently fail to match the account while
    // the surface still confirms a send. A blank-after-trim value is a no-op.
    const trimmed = email.trim();
    if (trimmed === '') return;
    sentRef.current = true;
    // Flip to the generic confirmation FIRST — this moves focus to the new
    // heading via the [submitted] effect above. Then DEFER the polite live-region
    // text to the next animation frame so the announcement lands AFTER focus has
    // settled: several screen readers (VoiceOver/NVDA) drop a queued polite
    // announcement when focus is moved programmatically in the same tick, which
    // would silently swallow the AC1 confirmation (Story 6.26 review, ADO-1205).
    // Enumeration-safety is unchanged — the message and the fire-and-forget send
    // are identical for any input; only the announce timing shifts by one frame.
    setSubmitted(true);
    announceFrameRef.current = requestAnimationFrame(() => {
      announceFrameRef.current = null;
      setAnnouncement(confirmationMessage);
    });
    void onRequest?.(trimmed);
  };

  const onUseDifferentEmail = () => {
    // Release the permanent latch and clear the announcement so a subsequent
    // submit both fires again and re-announces; the email value is preserved so
    // the typo can be edited. The focus effect refocuses the form heading (AC2).
    // Cancel a still-pending announce frame so a stale message cannot land on the
    // freshly-reset form.
    if (announceFrameRef.current !== null) {
      cancelAnimationFrame(announceFrameRef.current);
      announceFrameRef.current = null;
    }
    sentRef.current = false;
    setAnnouncement('');
    setSubmitted(false);
  };

  return {
    email,
    onEmailChange: setEmail,
    submitted,
    announcement,
    headingRef,
    onSubmit,
    onUseDifferentEmail,
  };
}
